import type { FormDoc, LetterDoc, SessionView, SignerStep, UploadDoc } from "@gsignatures/core";
import type { SignerApi } from "../api/types";
import type { Who } from "../strings";

export type Screen = "consent" | "offer" | "tax" | "upload" | "review" | "sign" | "done";
export type ModalKind = "help" | "paper" | "decline" | "download";

/** The screen that shows a server step. Earlier screens can be revisited until signing. */
export function screenFor(step: SignerStep): Screen {
  switch (step.step) {
    case "verify":
    case "consent":
      return "consent";
    case "read":
      return "offer";
    case "chooseForm":
    case "form":
      return "tax";
    case "upload":
      return "upload";
    case "review":
      return "review";
    case "sign":
      return "sign";
    case "done":
      return "done";
  }
}

const ORDER: Screen[] = ["consent", "offer", "tax", "upload", "review", "sign", "done"];
export const screenRank = (s: Screen) => ORDER.indexOf(s);

/** Position in the five-step bar: verify 0 … review & sign 4, done 5. */
export function stepIndexFor(screen: Screen): number {
  return { consent: 1, offer: 2, tax: 3, upload: 3, review: 4, sign: 4, done: 5 }[screen];
}

/** Every letter, in the order they're read and signed (an offer letter, then an agreement). */
export const lettersOf = (s: SessionView): LetterDoc[] => s.documents.filter((d): d is LetterDoc => d.kind === "letter");
export const formOf = (s: SessionView): FormDoc | undefined => s.documents.find((d): d is FormDoc => d.kind === "form");
/** Pictures to add (an ID proof), if the sender asked for any. */
export const uploadsOf = (s: SessionView): UploadDoc[] => s.documents.filter((d): d is UploadDoc => d.kind === "upload");

/** What every screen inside the flow gets from the page. */
export interface FlowCtx {
  api: SignerApi;
  session: SessionView;
  w: Who;
  /** Runs an API call; failures are handled once, centrally (dead link, session ended, offline…). Undefined on failure. */
  run: <T>(fn: () => Promise<T>) => Promise<T | undefined>;
  /** Takes a fresh session from the server and moves to the screen its step calls for. */
  apply: (s: SessionView) => void;
  /** Takes a fresh session without moving. */
  update: (s: SessionView) => void;
  go: (s: Screen) => void;
  /** Opens one letter (from the review). */
  viewLetter: (documentId: string) => void;
  /** From an already-accepted letter: the next letter, or on to the tax form. */
  continueFrom: (documentId: string) => void;
  toast: (m: string) => void;
  openModal: (m: ModalKind) => void;
}
